import type React from 'react';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { BELOW_LG } from '@/src/lib/breakpoints';
import { useBrowserOverlayHistory } from './useBrowserOverlayHistory';

const shouldManageMobileBrowserHistory = () => window.matchMedia(BELOW_LG).matches;

/**
 * FullscreenOverlay Context for managing open/close state
 */
type FullscreenOverlayContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  onOpenChange?: (open: boolean) => void;
  triggerRef: React.MutableRefObject<{
    element: HTMLElement | null;
    keyboard: boolean;
    scrollY: number | null;
  }>;
};

const FullscreenOverlayContext = createContext<FullscreenOverlayContextValue | null>(null);

/**
 * Hook to access FullscreenOverlay context
 */
export const useFullscreenOverlay = () => {
  const context = useContext(FullscreenOverlayContext);
  if (!context) {
    throw new Error('useFullscreenOverlay must be used within a FullscreenOverlay.Root');
  }
  return context;
};

/**
 * FullscreenOverlay Context Provider props
 */
type FullscreenOverlayProviderProps = {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * FullscreenOverlay Context Provider
 *
 * Manages the open/close state for the overlay using either controlled or uncontrolled patterns.
 * Follows React patterns similar to form inputs - supports both controlled (open prop)
 * and uncontrolled (defaultOpen prop) usage.
 *
 * On mobile, pushes a synthetic browser history entry when the overlay opens so that
 * swiping back closes the overlay instead of navigating away from the page.
 */
export const FullscreenOverlayProvider = ({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
}: FullscreenOverlayProviderProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [triggerRef] = useState(() => ({
    current: {
      element: null as HTMLElement | null,
      keyboard: false,
      scrollY: null as number | null,
    },
  }));

  // Use controlled value if provided, otherwise use internal state
  const isOpen = open !== undefined ? open : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    // Update internal state if uncontrolled
    if (open === undefined) {
      setInternalOpen(newOpen);
    }

    // Call external callback if provided
    onOpenChange?.(newOpen);
  };

  const close = useBrowserOverlayHistory({
    enabled: shouldManageMobileBrowserHistory,
    onClose: () => handleOpenChange(false),
    open: isOpen,
    prefix: 'fullscreen-overlay',
  });

  const contextValue: FullscreenOverlayContextValue = {
    isOpen,
    open: () => handleOpenChange(true),
    close,
    onOpenChange: handleOpenChange,
    triggerRef,
  };

  return <FullscreenOverlayContext.Provider value={contextValue}>{children}</FullscreenOverlayContext.Provider>;
};
